import { useState, useRef, useCallback } from 'react';

export const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB, matches the backend limit

/** Why a file can't be analyzed, or null if it can */
export const audioFileProblem = (file) => {
  if (!file.type.startsWith('audio/')) return 'Please choose an audio file (WAV, MP3, FLAC, etc.).';
  if (file.size > MAX_FILE_SIZE) return 'This file is too large. The limit is 100 MB.';
  return null;
};

// The clip waiting to be analyzed, chosen with the file picker or by
// drag-and-drop. Nothing is accepted unless canUpload, and drops are ignored
// while isBusy (an analysis is running).
export const useAudioFileInput = ({ canUpload, isBusy, setError }) => {
  const [file, setFile] = useState(null);
  const [stagedAt, setStagedAt] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = useCallback((selectedFile) => {
    if (!canUpload) return;
    if (!selectedFile) return;
    const problem = audioFileProblem(selectedFile);
    if (problem) {
      setError(problem);
      return;
    }

    setFile(selectedFile);
    setStagedAt(Date.now());
    setError(null);
  }, [canUpload, setError]);

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    if (canUpload && !isBusy) setIsDragging(true);
  }, [canUpload, isBusy]);

  const onDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    if (!canUpload || isBusy) return;

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  }, [canUpload, isBusy, handleFileSelect]);

  return {
    file, setFile,
    stagedAt,
    isDragging,
    fileInputRef,
    handleFileSelect,
    onDragOver,
    onDragLeave,
    onDrop
  };
};
